import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { isValid, parse } from 'date-fns';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { SitesDropdown } from '@/components';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { Selection } from '@/components/common/sites-drop-down';
import { CLEAR_VALUE, TIMEZONES } from '@/constants';
import { FARBRO_SITE_ID } from '@/pages/aspiration/utils/constants';
import { getValidSites } from '@/services/auth';
import { AspirationSite, GET_EQUIPMENTS } from '@/types.gql';
import { formatDateWithPattern } from '@/utils';

import { FARBRO_LOCATIONS } from '../../utils/constants';

import './OpuSearchSelector.css';

const toUrlDate = (d: Date) => formatDateWithPattern(d, 'yyyy-MM-dd');
const fromUrlDate = (s: string) => parse(s, 'yyyy-MM-dd', new Date());

type Option = { label: string; value: string };
type AfterFn = (p: URLSearchParams) => void;

const DropdownField = (props: React.ComponentProps<typeof Dropdown>) => (
  <div className="opu-search-dropdown-spacer">
    <Dropdown virtualScrollerOptions={{ itemSize: 36 }} {...props} />
  </div>
);

export type OPUSearchSelectorProps = {
  siteData?: AspirationSite[];
};

export const OpuSearchSelector = ({ siteData }: OPUSearchSelectorProps) => {
  const [params, setParams] = useSearchParams();

  useLayoutEffect(() => {
    if (!params.get('date')) {
      const p = new URLSearchParams(params);
      p.set('date', toUrlDate(new Date()));
      setParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opuDateStr = params.get('date') ?? toUrlDate(new Date());
  const opuSiteId = params.get('site') ?? '';
  const techId = params.get('tech') ?? '';
  const location = params.get('location') ?? '';
  const timeZone = params.get('tz') ?? '';

  const [date, setDate] = useState<Date>(() => {
    const d = fromUrlDate(opuDateStr);
    return isValid(d) ? d : new Date();
  });
  const me = useAppSelector((state) => state.authentication.me);

  const validSites = getValidSites(me);

  const updateParams = useCallback(
    (patch: (p: URLSearchParams) => void) => {
      const p = new URLSearchParams(params);
      patch(p);
      setParams(p);
    },
    [params, setParams],
  );

  const makeHandler =
    (key: string, clearValue?: string, after?: AfterFn) =>
    (e: DropdownChangeEvent) =>
      updateParams((p) => {
        if (e.value && (!clearValue || e.value !== clearValue))
          p.set(key, e.value);
        else p.delete(key);
        after?.(p);
      });

  const handleSiteChange = useCallback((site: Selection | null) => {}, []);
  const handleTechChange = makeHandler('tech');
  const handleLocationChange = makeHandler('location');
  const handleTZChange = makeHandler('tz', CLEAR_VALUE);

  const handleDateChange = (e: { value?: Date | null }) => {
    const d = e.value ?? null;
    setDate(d ?? new Date());
    updateParams((p) => p.set('date', toUrlDate(d ?? new Date())));
  };

  const { data: techData } = useQuery(GET_EQUIPMENTS);

  const technicians: Option[] = useMemo(
    () =>
      (techData as any)?.equipments?.items?.map((t: any) => ({
        label: t.name,
        value: t.id,
      })) ?? [],
    [techData],
  );

  const dropdowns = useMemo(
    () => [
      {
        show: true,
        type: 'site',
        component: (
          <SitesDropdown
            className="w-full"
            field="siteName"
            onChange={handleSiteChange}
            paramKey="site"
            validSites={validSites}
          />
        ),
      },
      {
        show: opuSiteId === FARBRO_SITE_ID,
        props: {
          value: FARBRO_LOCATIONS.includes(location) ? location : undefined,
          onChange: handleLocationChange,
          options: FARBRO_LOCATIONS,
          placeholder: 'Location',
          tabIndex: 3,
        },
      },
      {
        show: true,
        props: {
          value: techId,
          onChange: handleTechChange,
          options: technicians,
          filter: true,
          filterBy: 'label',
          placeholder: 'Embryologist',
          tooltip: 'Embryologist',
          tabIndex: 4,
        },
      },
      {
        show: true,
        props: {
          value: timeZone,
          onChange: handleTZChange,
          options: TIMEZONES,
          placeholder: 'Original Time Zone',
          tooltip: timeZone ? 'Change Time Zone' : 'Choose Original Time Zone',
        },
      },
    ],
    [
      opuSiteId,
      techId,
      location,
      timeZone,
      validSites,
      technicians,
      handleSiteChange,
      handleTechChange,
      handleLocationChange,
      handleTZChange,
    ],
  );

  return (
    <div className="flex items-center gap-2">
      <div className="opu-search-dropdown-spacer">
        <Calendar
          value={date}
          dateFormat="mm-dd-yy"
          onChange={handleDateChange}
          showIcon
          placeholder="OPU Date"
          tabIndex={1}
        />
      </div>
      {dropdowns
        .filter((d) => d.show)
        .map((d, idx) =>
          d.type === 'site' ? (
            <div key={idx}>{d.component}</div>
          ) : (
            <DropdownField key={idx} {...d.props} />
          ),
        )}
    </div>
  );
};
